import type { FetchFunction } from 'relay-runtime';

export type MiddlewareFactory = (fetchFn: FetchFunction) => FetchFunction;

export interface MiddlewareConfig {
	name: string;
	factory: MiddlewareFactory;
	enabled: boolean;
}

/**
 * Composes middleware functions into a single fetch function.
 *
 * Each middleware wraps the accumulator, so the **last** enabled entry ends up outermost and the
 * first ends up closest to the network. Requests travel outermost → innermost and errors travel back
 * innermost → outermost, which is what decides who gets to see (or swallow) an error first.
 *
 * This is the reverse of Apollo's `ApolloLink.from([...])`, where the *first* entry is outermost — so
 * a chain ported from `createClient` must be listed here in reverse to preserve its semantics.
 */
export const composeMiddlewares = (
	baseFetch: FetchFunction,
	middlewares: MiddlewareConfig[],
): FetchFunction => {
	return middlewares
		.filter((middleware) => middleware.enabled)
		.reduce((composedFetch, middleware) => {
			return middleware.factory(composedFetch);
		}, baseFetch);
};
